import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, List, Modal, Pagination, Spin, Empty } from 'antd';
import { NotificationOutlined, CalendarOutlined, ShoppingOutlined, EnvironmentOutlined, LinkOutlined } from '@ant-design/icons';
import AuthService from '../../services/authService';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import dayjs from 'dayjs';

const { Text } = Typography;

const PAGE_SIZE = 5;

const TopTag = () => (
    <span
        style={{
            background: '#fff7e6',
            color: '#fa8c16',
            border: '1px solid #ffd591',
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}
    >
        TOP
    </span>
);

const NewTag = () => (
    <span
        style={{
            background: '#fff1f0',
            color: '#f5222d',
            border: '1px solid #ffa39e',
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}
    >
        NEW
    </span>
);

const ImportantTag = () => (
    <span
        style={{
            background: '#fff1f0',
            color: '#f5222d',
            border: '1px solid #ffa39e',
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}
    >
        중요
    </span>
);

const SERVICE_TAG_COLORS = {
    '공지': { bg: '#e6fffb', color: '#08979c', border: '#87e8de' },
    '공통': { bg: '#e6f4ff', color: '#1677ff', border: '#91caff' },
    '플레이스': { bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' },
    '교육': { bg: '#f9f0ff', color: '#722ed1', border: '#d3adf7' },
};

const ServiceTag = ({ tag }) => {
    const style = SERVICE_TAG_COLORS[tag] || { bg: '#f5f5f5', color: '#595959', border: '#d9d9d9' };
    return (
        <span style={{
            background: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}>
            {tag}
        </span>
    );
};

const NaverNoticeCard = ({ title, icon, accentColor, items, loading, linkUrl }) => (
    <Card
        title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {icon}
                    <span style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{title}</span>
                </div>
                <a
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        textDecoration: 'none',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    전체보기 <LinkOutlined />
                </a>
            </div>
        }
        style={{
            borderRadius: 12,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            border: 'none',
            overflow: 'hidden',
        }}
        styles={{
            header: {
                background: accentColor,
                border: 'none',
                padding: '0 24px',
                minHeight: 52,
            },
            body: { padding: 0 },
        }}
    >
        <Spin spinning={loading}>
            {!loading && items.length === 0 ? (
                <div style={{ padding: '28px 0', textAlign: 'center' }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="공지사항을 불러올 수 없습니다." />
                    <a
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block',
                            marginTop: 12,
                            padding: '6px 20px',
                            borderRadius: 8,
                            background: accentColor,
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 600,
                            textDecoration: 'none',
                        }}
                    >
                        공지사항 바로가기
                    </a>
                </div>
            ) : (
                <List
                    itemLayout="horizontal"
                    dataSource={items}
                    renderItem={item => (
                        <List.Item
                            style={{
                                padding: '11px 24px',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f5f7ff')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                {item.isPinned && <ImportantTag />}
                                {item.isNew && <NewTag />}
                                {item.serviceTag && <ServiceTag tag={item.serviceTag} />}
                                <Text
                                    style={{
                                        fontSize: 14,
                                        color: '#0d1b2a',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {item.title}
                                </Text>
                            </div>
                            {item.date && (
                                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 12 }}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    {dayjs(item.date).format('YYYY.MM.DD')}
                                </Text>
                            )}
                        </List.Item>
                    )}
                />
            )}
        </Spin>
    </Card>
);

const DashBoard = () => {
    const [notices, setNotices] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeNotice, setActiveNotice] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [pinnedDetails, setPinnedDetails] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

    const [shoppingNotices, setShoppingNotices] = useState([]);
    const [shoppingLoading, setShoppingLoading] = useState(false);
    const [smartplaceNotices, setSmartplaceNotices] = useState([]);
    const [smartplaceLoading, setSmartplaceLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 576);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchNotices = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const res = await AuthService.authAxios.get('/notices', {
                params: { page: p, limit: PAGE_SIZE },
            });
            const list = res.data.list || [];
            setNotices(list);
            setTotal(res.data.total || 0);

            const expandable = list.filter(n => n.is_notice);
            if (expandable.length > 0) {
                const details = {};
                await Promise.all(
                    expandable.map(async n => {
                        try {
                            const dr = await AuthService.authAxios.get(`/notices/${n.id}`);
                            details[String(n.id)] = dr.data;
                        } catch {
                            details[String(n.id)] = null;
                        }
                    })
                );
                setPinnedDetails(details);
            }
        } catch {
            setNotices([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchNaverShoppingNotices = useCallback(async () => {
        setShoppingLoading(true);
        try {
            const res = await AuthService.authAxios.get('/notices/naver/shopping', {
                params: { page: 1, pageSize: 10 },
            });
            setShoppingNotices(res.data.items || []);
        } catch {
            setShoppingNotices([]);
        } finally {
            setShoppingLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotices(page);
    }, [fetchNotices, page]);

    const fetchNaverSmartplaceNotices = useCallback(async () => {
        setSmartplaceLoading(true);
        try {
            const res = await AuthService.authAxios.get('/notices/naver/smartplace');
            setSmartplaceNotices(res.data.items || []);
        } catch {
            setSmartplaceNotices([]);
        } finally {
            setSmartplaceLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNaverShoppingNotices();
    }, [fetchNaverShoppingNotices]);

    useEffect(() => {
        fetchNaverSmartplaceNotices();
    }, [fetchNaverSmartplaceNotices]);

    const openNotice = async id => {
        setModalOpen(true);
        setDetailLoading(true);
        try {
            const res = await AuthService.authAxios.get(`/notices/${id}`);
            setActiveNotice(res.data);
        } catch {
            setActiveNotice(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setActiveNotice(null);
    };

    const isNew = date => dayjs().diff(dayjs(date), 'day') <= 7;

    const pinnedNotices = notices.filter(n => n.is_notice);
    const regularNotices = notices.filter(n => !n.is_notice);

    return (
        <div style={{ padding: '24px' }}>
            {/* 오픈채팅 유도 배너 */}
            <style>{`
                @keyframes adcare-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.6; transform: scale(1.15); }
                }
                @keyframes adcare-shine {
                    0% { left: -100%; }
                    100% { left: 200%; }
                }
                @keyframes adcare-marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .adcare-marquee-track::-webkit-scrollbar { display: none; }
                .adcare-chat-banner:hover {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 8px 28px rgba(255, 193, 7, 0.55) !important;
                }
                .adcare-chat-banner:hover .adcare-chat-btn {
                    background: #fff !important;
                    color: #b37400 !important;
                }
                .adcare-chat-banner .adcare-shine {
                    position: absolute;
                    top: 0; left: -100%;
                    width: 60%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
                    animation: adcare-shine 2.8s ease-in-out infinite;
                    pointer-events: none;
                }
            `}</style>
            <a
                href="https://open.kakao.com/o/gJXNIbui"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', textDecoration: 'none', marginBottom: 16 }}
            >
                <div
                    className="adcare-chat-banner"
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(120deg, #FFE500 0%, #FFD000 45%, #FFBA00 100%)',
                        borderRadius: 14,
                        padding: '18px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 4px 20px rgba(255, 193, 7, 0.4)',
                        gap: 16,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                >
                    <div className="adcare-shine" />

                    {/* 카카오톡 아이콘 (공통) */}
                    <img
                        src="/assets/images/talk_icon.png"
                        alt="KakaoTalk"
                        style={{
                            width: isMobile ? 44 : 56,
                            height: isMobile ? 44 : 56,
                            borderRadius: isMobile ? 12 : 16,
                            flexShrink: 0,
                            boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
                            objectFit: 'cover',
                        }}
                    />

                    {isMobile ? (
                        /* ── 모바일: 상단 카피+버튼 / 하단 태그 가로스크롤 ── */
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            background: '#ff3b30', color: '#fff',
                                            fontSize: 9.5, fontWeight: 800,
                                            padding: '1px 6px', borderRadius: 20,
                                            flexShrink: 0,
                                        }}>
                                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', animation: 'adcare-pulse 1.4s ease-in-out infinite', display: 'inline-block' }} />
                                            LIVE
                                        </span>
                                        <span style={{ fontSize: 11, color: '#7a5500', fontWeight: 700 }}>셀러 오픈채팅방</span>
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 900, color: '#1a0f00', letterSpacing: '-0.3px', lineHeight: 1.3 }}>
                                        순위 올리고 싶다면? 지금 참여하세요 🔥
                                    </div>
                                </div>
                                <div className="adcare-chat-btn" style={{
                                    background: '#1a0f00', color: '#FFD000',
                                    borderRadius: 8, padding: '8px 14px',
                                    fontSize: 12, fontWeight: 800,
                                    whiteSpace: 'nowrap', flexShrink: 0,
                                    textAlign: 'center', transition: 'background 0.2s, color 0.2s',
                                }}>
                                    참여하기 →
                                </div>
                            </div>
                            {/* 태그 무한 마퀴 */}
                            <div style={{ overflow: 'hidden', width: '100%' }}>
                                <div
                                    className="adcare-marquee-track"
                                    style={{
                                        display: 'flex', gap: 6, flexWrap: 'nowrap',
                                        animation: 'adcare-marquee 18s linear infinite',
                                        width: 'max-content',
                                    }}
                                >
                                    {[
                                        { emoji: '📊', text: '실시간 리워드슬롯 정보' },
                                        { emoji: '🎯', text: '시장 반영 리워드슬롯 안내' },
                                        { emoji: '📈', text: '시장분석 인사이트' },
                                        { emoji: '🏆', text: '순위상승 노하우' },
                                        { emoji: '🛒', text: '순위상승 상품 안내' },
                                        { emoji: '💡', text: '광고·키워드 전략' },
                                        { emoji: '🆕', text: '신규 업데이트 정보' },
                                        { emoji: '📝', text: '개선 요청 사항' },
                                        { emoji: '🤝', text: '셀러 네트워킹' },
                                    ].concat([
                                        { emoji: '📊', text: '실시간 리워드슬롯 정보' },
                                        { emoji: '🎯', text: '시장 반영 리워드슬롯 안내' },
                                        { emoji: '📈', text: '시장분석 인사이트' },
                                        { emoji: '🏆', text: '순위상승 노하우' },
                                        { emoji: '🛒', text: '순위상승 상품 안내' },
                                        { emoji: '💡', text: '광고·키워드 전략' },
                                        { emoji: '🆕', text: '신규 업데이트 정보' },
                                        { emoji: '📝', text: '개선 요청 사항' },
                                        { emoji: '🤝', text: '셀러 네트워킹' },
                                    ]).map(({ emoji, text }, i) => (
                                        <span key={i} style={{
                                            background: 'rgba(0,0,0,0.1)', color: '#3d2600',
                                            fontSize: 11, fontWeight: 700,
                                            padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                        }}>
                                            {emoji} {text}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* ── PC: 풀 레이아웃 ── */
                        <>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* LIVE 뱃지 */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        background: '#ff3b30', color: '#fff',
                                        fontSize: 10.5, fontWeight: 800,
                                        padding: '2px 8px', borderRadius: 20,
                                        letterSpacing: '0.5px', textTransform: 'uppercase',
                                    }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'adcare-pulse 1.4s ease-in-out infinite', display: 'inline-block' }} />
                                        LIVE
                                    </span>
                                    <span style={{ fontSize: 11.5, color: '#7a5500', fontWeight: 600 }}>
                                        지금 셀러들이 정보를 나누고 있어요
                                    </span>
                                </div>
                                {/* 메인 카피 */}
                                <div style={{ fontSize: 17, fontWeight: 900, color: '#1a0f00', letterSpacing: '-0.5px', lineHeight: 1.25, marginBottom: 5 }}>
                                    순위 올리고 싶다면? 오픈채팅방으로 오세요 🔥
                                </div>
                                {/* 슬롯 설명 */}
                                <div style={{ fontSize: 11.5, color: '#5c3d00', fontWeight: 600, marginBottom: 8, lineHeight: 1.4 }}>
                                    💡 <b>리워드슬롯</b>이란? 실제 고객이 미션을 수행하며 네이버 쇼핑 순위를 끌어올리는 마케팅 자리입니다.
                                </div>
                                {/* 태그 칩 */}
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {[
                                        { emoji: '📊', text: '실시간 리워드슬롯 정보' },
                                        { emoji: '🎯', text: '시장 반영 리워드슬롯 안내' },
                                        { emoji: '📈', text: '시장분석 인사이트' },
                                        { emoji: '🏆', text: '순위상승 노하우' },
                                        { emoji: '🛒', text: '순위상승 상품 안내' },
                                        { emoji: '💡', text: '광고·키워드 전략' },
                                        { emoji: '🆕', text: '신규 업데이트 정보' },
                                        { emoji: '📝', text: '개선 요청 사항' },
                                        { emoji: '🤝', text: '셀러 네트워킹' },
                                    ].map(({ emoji, text }) => (
                                        <span key={text} style={{
                                            background: 'rgba(0,0,0,0.1)', color: '#3d2600',
                                            fontSize: 11.5, fontWeight: 700,
                                            padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
                                        }}>
                                            {emoji} {text}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            {/* CTA 버튼 */}
                            <div className="adcare-chat-btn" style={{
                                background: '#1a0f00', color: '#FFD000',
                                borderRadius: 10, padding: '12px 22px',
                                fontSize: 14, fontWeight: 800,
                                whiteSpace: 'nowrap', flexShrink: 0,
                                letterSpacing: '-0.3px', lineHeight: 1.3,
                                textAlign: 'center', transition: 'background 0.2s, color 0.2s',
                            }}>
                                무료 참여하기<br />
                                <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.7 }}>카카오 오픈채팅 →</span>
                            </div>
                        </>
                    )}
                </div>
            </a>

            {/* ADCARE 공지사항 */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <NotificationOutlined
                            style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>
                            ADCARE 공지사항
                        </span>
                    </div>
                }
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
                    border: 'none',
                    overflow: 'hidden',
                }}
                styles={{
                    header: {
                        background: '#001529',
                        border: 'none',
                        padding: '0 24px',
                        minHeight: 52,
                    },
                    body: { padding: 0 },
                }}
            >
                <Spin spinning={loading}>
                    {notices.length === 0 && !loading ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="등록된 공지사항이 없습니다."
                            style={{ padding: '48px 0' }}
                        />
                    ) : (
                        <>
                            {/* 펼치기 공지 */}
                            {pinnedNotices.map((item, idx) => (
                                <div
                                    key={item.id}
                                    style={{
                                        borderBottom:
                                            idx < pinnedNotices.length - 1 ||
                                            regularNotices.length > 0
                                                ? '1px solid #f0f0f0'
                                                : 'none',
                                    }}
                                >
                                    {/* 제목 행 */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '13px 24px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f0f0f0',
                                            background: '#fff',
                                            transition: 'background 0.15s',
                                        }}
                                        onClick={() => openNotice(item.id)}
                                        onKeyDown={e => e.key === 'Enter' && openNotice(item.id)}
                                        onMouseEnter={e =>
                                            (e.currentTarget.style.background = '#f5f7ff')
                                        }
                                        onMouseLeave={e =>
                                            (e.currentTarget.style.background = '#fff')
                                        }
                                    >
                                        {item.is_top ? <TopTag /> : null}
                                        {isNew(item.created_at) ? <NewTag /> : null}
                                        <Text
                                            strong
                                            style={{ fontSize: 15, flex: 1, color: '#0d1b2a' }}
                                        >
                                            {item.title}
                                        </Text>
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 12,
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {dayjs(item.created_at).format('YYYY.MM.DD')}
                                        </Text>
                                    </div>

                                    {/* 내용 */}
                                    <div style={{ padding: '20px 28px', background: '#fafbfc' }}>
                                        {pinnedDetails[String(item.id)] === undefined ? (
                                            <div style={{ textAlign: 'center', padding: '12px 0' }}>
                                                <Spin />
                                            </div>
                                        ) : pinnedDetails[String(item.id)] === null ? (
                                            <Empty description="내용을 불러올 수 없습니다." />
                                        ) : (
                                            <div
                                                className="notice-content"
                                                dangerouslySetInnerHTML={{
                                                    __html: pinnedDetails[String(item.id)].content,
                                                }}
                                                style={{
                                                    fontSize: 14,
                                                    lineHeight: 1.9,
                                                    wordBreak: 'break-word',
                                                    color: '#333',
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* 일반 공지 리스트 */}
                            {regularNotices.length > 0 && (
                                <>
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={regularNotices}
                                        renderItem={item => (
                                            <List.Item
                                                actions={[
                                                    <Text
                                                        key="date"
                                                        type="secondary"
                                                        style={{
                                                            fontSize: 12,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <CalendarOutlined
                                                            style={{ marginRight: 4 }}
                                                        />
                                                        {dayjs(item.created_at).format(
                                                            'YYYY.MM.DD'
                                                        )}
                                                    </Text>,
                                                ]}
                                                style={{
                                                    padding: '13px 24px',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e =>
                                                    (e.currentTarget.style.background = '#f5f7ff')
                                                }
                                                onMouseLeave={e =>
                                                    (e.currentTarget.style.background =
                                                        'transparent')
                                                }
                                                onClick={() => openNotice(item.id)}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    {item.is_top ? <TopTag /> : null}
                                                    {isNew(item.created_at) ? <NewTag /> : null}
                                                    <Text
                                                        strong
                                                        style={{
                                                            fontSize: 15,
                                                            color: '#0d1b2a',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Text>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                    {total > PAGE_SIZE && (
                                        <div
                                            style={{
                                                padding: '12px 24px',
                                                textAlign: 'right',
                                                borderTop: '1px solid #f0f0f0',
                                            }}
                                        >
                                            <Pagination
                                                current={page}
                                                total={total}
                                                pageSize={PAGE_SIZE}
                                                size="small"
                                                showSizeChanger={false}
                                                onChange={p => setPage(p)}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Spin>
            </Card>

            {/* 네이버 공지사항 2열 */}
            <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 400px', minWidth: 0 }}>
                    <NaverNoticeCard
                        title="네이버 쇼핑 공지사항"
                        icon={<ShoppingOutlined style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }} />}
                        accentColor="#03C75A"
                        items={shoppingNotices}
                        loading={shoppingLoading}
                        linkUrl="https://notice.naver.com/notices/shopping?page=1&pageSize=10&newNoticeHour=168&t=l"
                    />
                </div>
                <div style={{ flex: '1 1 400px', minWidth: 0 }}>
                    <NaverNoticeCard
                        title="네이버 스마트플레이스 공지사항"
                        icon={<EnvironmentOutlined style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }} />}
                        accentColor="#1EC800"
                        items={smartplaceNotices}
                        loading={smartplaceLoading}
                        linkUrl="https://new.smartplace.naver.com/notices"
                    />
                </div>
            </div>

            {/* 공지 상세 모달 */}
            <Modal
                open={modalOpen}
                onCancel={closeModal}
                footer={null}
                title={
                    activeNotice ? (
                        <div>
                            <div
                                style={{
                                    fontWeight: 700,
                                    fontSize: 16,
                                    lineHeight: 1.5,
                                    color: '#0d1b2a',
                                }}
                            >
                                {activeNotice.title}
                            </div>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {dayjs(activeNotice.created_at).format('YYYY.MM.DD HH:mm')}
                            </Text>
                        </div>
                    ) : (
                        '공지사항'
                    )
                }
                width={720}
                centered
                styles={{ body: { padding: '24px', maxHeight: '60vh', overflowY: 'auto' } }}
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                ) : activeNotice ? (
                    <div
                        className="notice-content"
                        dangerouslySetInnerHTML={{ __html: activeNotice.content }}
                        style={{
                            fontSize: 14,
                            lineHeight: 1.9,
                            wordBreak: 'break-word',
                            color: '#333',
                        }}
                    />
                ) : (
                    <Empty description="내용을 불러올 수 없습니다." />
                )}
            </Modal>

            <KakaoFloatButton />
        </div>
    );
};

export { DashBoard };
